// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import React, {
  useState,
  useCallback,
  useMemo,
} from 'react'

import {
  Text,
  Box,
  Captcha,
  CheckBox,
  Button,
  ThemeContextExtend,
  Modal,
} from '@usap/uicomponents'

import captchaConfig from '../../../../../../../config/captcha-config'
import SuccessMedium from '../../../Notification/Types/SuccessMedium'
import ErrorMedium from '../../../Notification/Types/ErrorMedium'
import { usePopUpContext } from '../../../../providers/PopUpProvider'
import InputText from './InputText'
import useGetInvalidFields from './hooks/useGetInvalidFields'
import useSendEmail from './hooks/useSendEmail'
import style from './style/constant'
import useWmoEnabled from '../../../../../../hooks/useWmoEnabled'
import NotifyFormWmo from './NotifyFormWmo'

const NotifyForm = () => {
  const { siteKey } = captchaConfig

  const {
    outOfStockItems,
    showOosNotifyForm,
    shouldShowOosNotifyForm,
  } = usePopUpContext()

  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errors, setError] = useState({})
  const [checked, setChecked] = useState(false)
  const [captcha, setCaptcha] = useState(false)
  const [email, setEmail] = useState('')
  const [isSent, setIsSent] = useState(false)

  const successMessage = `Thank you. You will receive an email
    when the item becomes available.`

  const failedMessage = `Oops! Something went wrong.
   <a href="/help-center/contact-us" target="_blank"><u>Contact us</u></a>
   for assistance.`

  const buttonLabel = useMemo(
    () => {
      if (isProcessing) return 'Sending...'
      return 'Notify Me'
    },
    [isProcessing],
  )

  const handleCheckBox = useCallback(
    event => setChecked(event.target.checked),
    [setChecked],
  )

  const handleChangeEmail = useCallback(
    event => {
      const { value } = event.target
      setEmail(value)
      setError({})
    },
    [setEmail],
  )

  const getInvalidFields = useGetInvalidFields({
    email, captcha, errors,
  })

  const handleSendEmail = useSendEmail({
    setIsProcessing,
    setIsFormSubmitted,
    getInvalidFields,
    setError,
    outOfStockItems,
    email,
    captcha,
    setIsSent,
  })

  // const handleRetry = useCallback(
  //   () => {
  //     setIsFormSubmitted(false)
  //   },
  //   [setIsFormSubmitted],
  // )

  const handleClose = useCallback(
    () => {
      setIsFormSubmitted(false)
      shouldShowOosNotifyForm(false)
    },
    [shouldShowOosNotifyForm, setIsFormSubmitted],
  )

  const isWmoEnabled = useWmoEnabled()
  if (isWmoEnabled) {
    const getModalHeader = (formSubmitted, sent) => {
      if (formSubmitted) {
        if (sent) {
          return 'Thank You!'
        }
        return 'Oops! Something went wrong.'
      }
      return 'Notify Me When Available'
    }
    return (
      <Modal
        header={getModalHeader(isFormSubmitted, isSent)}
        open={showOosNotifyForm}
        onOpenChange={shouldShowOosNotifyForm}
        content={(
          <NotifyFormWmo
            handleClose={handleClose}
            handleSendEmail={handleSendEmail}
            handleChangeEmail={handleChangeEmail}
            handleCheckBox={handleCheckBox}
            isFormSubmitted={isFormSubmitted}
            isSent={isSent}
            successMessage={successMessage}
            failedMessage={failedMessage}
            isProcessing={isProcessing}
            buttonLabel={buttonLabel}
            emailErrorMessage={errors.email}
            captcha={(
              <Captcha
                id="notifyme"
                name="notifyme"
                setCaptcha={setCaptcha}
                error={errors.captcha}
                siteKey={siteKey}
                reset={isFormSubmitted}
                // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
                style={{ pointerEvents: 'all' }}
              />
          )}
          />
        )}
      />
    )
  }

  return (
    <ThemeContextExtend value={style.form}>
      <Box pad="24px">
        {(isFormSubmitted && isSent) && (
          <Box width="100%" align="center">
            <SuccessMedium message={successMessage} />
            <Box pad="small">
              <Button
                primary
                custom
                hideBorder
                label="Continue shopping"
                onClick={handleClose}
              />
            </Box>
          </Box>
        )}
        {(isFormSubmitted && !isSent) && (
          <Box width="100%" align="center">
            <ErrorMedium message={failedMessage} />
            <Box pad="small">
              <Button
                primary
                custom
                hideBorder
                // label="Retry"
                // onClick={handleRetry}
                label="Close"
                onClick={handleClose}
              />
            </Box>
          </Box>
        )}
        {!isFormSubmitted && (
          <Box>
            <Box pad={style.textWrapperPad}>
              <Text size="small" color="dark-2" margin={style.textMargin}>
                Enter your email address below to be notified when items are back in stock
              </Text>
            </Box>
            <Box pad={style.inputTextWrapperPad}>
              <InputText
                label={(
                  <Text size="small" color="dark-2" weight="bold">
                    Email Address
                  </Text>
                )}
                id="emailAddress"
                name="emailAddress"
                type="email"
                placeholder="Enter email address"
                onChange={handleChangeEmail}
                value={email}
                error={errors.email}
                aria-label="email"
              />
            </Box>
            <Box pad={style.checkBoxWrapperPad}>
              <CheckBox
                checked={checked}
                label={(
                  <Text size="small" color="dark-2">
                    Send me CarParts exclusive offers and promotions.
                  </Text>
                )}
                onChange={handleCheckBox}
              />
            </Box>
            <Box pad={style.captchaWrapperPad} alignSelf="start">
              <Captcha
                id="notifyme"
                name="notifyme"
                setCaptcha={setCaptcha}
                error={errors.captcha}
                siteKey={siteKey}
                reset={isFormSubmitted}
              />
            </Box>
            <Box pad={style.buttonWrapperPad} width="100%">
              <Button
                id="notifyMe"
                primary
                custom
                hideBorder
                label={buttonLabel}
                width="100%"
                onClick={handleSendEmail}
                disabled={isProcessing}
              />
            </Box>
          </Box>
        )}
      </Box>
    </ThemeContextExtend>
  )
}

export default NotifyForm
